import { useCallback, useEffect, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";

import {
  WetSignatureCaptureMethodEnums,
  useCreateReportSectionFieldFileMutation,
  useUpdateReportSectionFieldFileUrlMutation,
} from "@api/graphql/types-and-hooks";
import { getFieldImageSignedUrl } from "@components/FieldComponent/utils";
import { ReportFieldDisabledValue } from "@components/ReportField/ReportFieldDisabledValue";
import { ReportFieldInputProps } from "@components/ReportField/types";
import { useUnsavedMarker } from "@components/ReportField/useUnsavedMarker";
import { notifyError } from "@components/notifications/notifications";
import { useReport } from "@providers/ReportProvider";

// Size of the saved PNG. Exports place the image at its pixel size (portal
// docx) or fit it to 300x120 (pipelines docx), so it stays 320x120 however
// wide the pad is drawn (PROA-2917). Strokes are recorded in these units.
export const DRAWN_SIGNATURE_IMAGE_WIDTH = 320;
export const DRAWN_SIGNATURE_IMAGE_HEIGHT = 120;

type Point = { x: number; y: number };
type Stroke = Point[];

// Give the pad one backing pixel per device pixel so strokes stay sharp on
// high-resolution screens, drawing in saved-image units. Resizing clears the
// canvas, so callers redraw the strokes after. A pad with no size yet keeps
// its 320x120 default and returns false.
const sizeBackingStore = (canvas: HTMLCanvasElement) => {
  const rect = canvas.getBoundingClientRect();
  if (!rect.width || !rect.height) return false;
  const ratio = window.devicePixelRatio || 1;
  canvas.width = Math.round(rect.width * ratio);
  canvas.height = Math.round(rect.height * ratio);
  canvas
    .getContext("2d")
    ?.setTransform(
      canvas.width / DRAWN_SIGNATURE_IMAGE_WIDTH,
      0,
      0,
      canvas.height / DRAWN_SIGNATURE_IMAGE_HEIGHT,
      0,
      0,
    );
  return true;
};

const setPen = (ctx: CanvasRenderingContext2D) => {
  ctx.lineWidth = 2;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  ctx.strokeStyle = "#000000";
};

// Draws recorded strokes, in saved-image units, onto a canvas context.
const drawStrokes = (ctx: CanvasRenderingContext2D, strokes: Stroke[]) => {
  setPen(ctx);
  for (const [first, ...rest] of strokes) {
    if (!first || rest.length === 0) continue;
    ctx.beginPath();
    ctx.moveTo(first.x, first.y);
    for (const point of rest) ctx.lineTo(point.x, point.y);
    ctx.stroke();
  }
};

// The saved PNG is drawn from the recorded strokes onto its own 320x120 canvas,
// so it never resamples the pad, whatever its size or pixel ratio.
const exportSignature = (strokes: Stroke[]) => {
  const image = document.createElement("canvas");
  image.width = DRAWN_SIGNATURE_IMAGE_WIDTH;
  image.height = DRAWN_SIGNATURE_IMAGE_HEIGHT;
  const context = image.getContext("2d");
  if (context) drawStrokes(context, strokes);
  return image;
};

// How long the pen stays up before the drawing saves on its own. Every new
// stroke restarts the wait, so a burst of strokes produces one save.
export const DRAWN_SIGNATURE_AUTOSAVE_DELAY_MS = 1000;

type SaveStatus = "idle" | "saving" | "saved" | "error";

const useResetDirtyFieldWhenServerContentChanges = (fieldId: string, content: string | null | undefined) => {
  const form = useFormContext<Record<string, string>>();
  const lastContentRef = useRef(content);
  useEffect(() => {
    if (lastContentRef.current === content) return;
    lastContentRef.current = content;
    if (form?.getFieldState(fieldId).isDirty) form.resetField(fieldId, { defaultValue: content ?? "" });
  }, [form, fieldId, content]);
};

interface ReportFieldDrawnSignatureProps extends ReportFieldInputProps {
  // Set by ReportFieldWetSignature when this component is reused as the
  // "drawn" method sub-widget of a WET_SIGNATURE field — see
  // ReportFieldImage's identical `captureMethod` prop for the full
  // rationale.
  captureMethod?: WetSignatureCaptureMethodEnums;
}

// A trackpad/touch signature pad, mirroring ReportFieldImage's structure:
// show the existing image if one's already been drawn, otherwise render the
// capture UI. Uploads through the same createReportSectionFieldFile /
// updateReportSectionFieldFileUrl signed-URL flow every other image field
// (IMAGE, and now the "drawn" method of WET_SIGNATURE via
// ReportFieldWetSignature) already uses. The drawing saves on its own once
// the pen has been up for DRAWN_SIGNATURE_AUTOSAVE_DELAY_MS (PROA-2913).
export const ReportFieldDrawnSignature: React.FC<ReportFieldDrawnSignatureProps> = ({
  onChange,
  field,
  disabled,
  captureMethod,
}) => {
  const { report, reportQuery } = useReport();
  const createFileMutation = useCreateReportSectionFieldFileMutation({ throwOnError: false });
  const updateFileUrlMutation = useUpdateReportSectionFieldFileUrlMutation({ throwOnError: false });

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const isDrawingRef = useRef(false);
  // Every stroke on the pad, in saved-image units. The pad and the saved image
  // are both drawn from these, so a resize just redraws them.
  const strokesRef = useRef<Stroke[]>([]);
  const hasInk = () => strokesRef.current.some((stroke) => stroke.length > 1);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const stopWatchingPixelRatioRef = useRef<(() => void) | null>(null);
  const attachCanvas = useCallback((canvas: HTMLCanvasElement | null) => {
    resizeObserverRef.current?.disconnect();
    resizeObserverRef.current = null;
    stopWatchingPixelRatioRef.current?.();
    stopWatchingPixelRatioRef.current = null;
    canvasRef.current = canvas;
    if (!canvas) return;
    // Re-size when the pad's size or the screen's pixel ratio changes, then
    // redraw the ink the resize erased.
    const resize = () => {
      if (!sizeBackingStore(canvas)) return;
      const ctx = canvas.getContext("2d");
      if (ctx) drawStrokes(ctx, strokesRef.current);
    };
    resize();
    if (typeof ResizeObserver !== "undefined") {
      const observer = new ResizeObserver(resize);
      observer.observe(canvas);
      resizeObserverRef.current = observer;
    }
    // ResizeObserver only sees CSS size, so moving to a screen with a different
    // pixel ratio needs its own listener. The query matches one ratio, so
    // re-arm it for the new ratio after each change.
    const watchPixelRatio = () => {
      if (typeof window.matchMedia !== "function") return;
      const query = window.matchMedia(`(resolution: ${window.devicePixelRatio || 1}dppx)`);
      const onChange = () => {
        resize();
        watchPixelRatio();
      };
      query.addEventListener("change", onChange, { once: true });
      stopWatchingPixelRatioRef.current = () => query.removeEventListener("change", onChange);
    };
    watchPixelRatio();
  }, []);
  // Bumped whenever a stroke adds ink, so a save can tell whether the pad
  // changed while it ran.
  const inkVersionRef = useRef(0);
  const isSavingRef = useRef(false);
  const unfinishedSavesRef = useRef(0);
  const isMountedRef = useRef(true);
  // True once a save from this pad succeeded, so Clear also removes it.
  const hasSavedRef = useRef(false);
  const autosaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>("idle");
  // Once the clinician draws here, keep the pad up even after a save stores
  // an image, so a pause mid-signature doesn't lock in half a signature.
  const [isPadActive, setIsPadActive] = useState(false);

  const cancelAutosave = () => {
    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current);
      autosaveTimerRef.current = null;
    }
  };

  const { mark: markUnsaved, clear: clearUnsaved } = useUnsavedMarker(`${field.id}:draft`);
  useResetDirtyFieldWhenServerContentChanges(field.id, field.content);

  // A drawing that hasn't started saving is dropped on unmount (switching
  // signature method, or the field turning read-only), as before.
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
      if (unfinishedSavesRef.current === 0) clearUnsaved();
    };
  }, [clearUnsaved]);

  const imageSignedUrl = getFieldImageSignedUrl(field);

  if (disabled) {
    if (imageSignedUrl) {
      return (
        <div className="flex items-center justify-center rounded-lg border border-dashed border-limestone-200 bg-limestone-50 p-2">
          <img src={imageSignedUrl} alt="Signature" className="max-h-24 object-contain" />
        </div>
      );
    }
    return <ReportFieldDisabledValue />;
  }

  if (imageSignedUrl && !isPadActive) {
    return (
      <div className="flex flex-col items-start gap-2">
        <div className="flex items-center justify-center rounded-lg border border-dashed border-limestone-200 bg-limestone-50 p-2">
          <img src={imageSignedUrl} alt="Signature" className="max-h-24 object-contain" />
        </div>
        <button
          type="button"
          className="text-sm text-blurple-500 underline"
          onClick={() =>
            onChange("", {
              onSuccess: async () => {
                await reportQuery.refetch();
              },
            })
          }
        >
          Redraw signature
        </button>
      </div>
    );
  }

  const getContext = () => canvasRef.current?.getContext("2d") ?? null;

  const pointFromEvent = (canvas: HTMLCanvasElement, event: React.PointerEvent<HTMLCanvasElement>) => {
    const rect = canvas.getBoundingClientRect();
    const scaleX = rect.width ? DRAWN_SIGNATURE_IMAGE_WIDTH / rect.width : 1;
    const scaleY = rect.height ? DRAWN_SIGNATURE_IMAGE_HEIGHT / rect.height : 1;
    return { x: (event.clientX - rect.left) * scaleX, y: (event.clientY - rect.top) * scaleY };
  };

  const saveDrawing = async () => {
    const canvas = canvasRef.current;
    // A save already running re-checks the pad when it finishes, so it
    // picks up anything drawn meanwhile.
    if (!canvas || isSavingRef.current) return;
    cancelAutosave();
    isSavingRef.current = true;
    unfinishedSavesRef.current += 1;
    setSaveStatus("saving");
    const inkVersionAtSave = inkVersionRef.current;
    try {
      const image = exportSignature(strokesRef.current);
      const blob: Blob | null = await new Promise((resolve) => image.toBlob(resolve, "image/png"));
      if (!blob) throw new Error("Could not export the drawn signature.");
      const file = new File([blob], "drawn-signature.png", { type: "image/png" });

      const created = await createFileMutation.mutateAsync({
        input: {
          reportId: report.id,
          fieldId: field.id,
          fileSize: file.size,
          mimeType: file.type,
          originalFileName: file.name,
        },
      });
      const uploadUrl = created?.createReportSectionFieldFile?.fileUrl;
      if (!uploadUrl) throw new Error("No signed upload URL returned.");

      const putResponse = await fetch(uploadUrl, {
        method: "PUT",
        headers: { "Content-Type": file.type },
        body: file,
      });
      if (!putResponse.ok) throw new Error(`S3 upload failed: ${putResponse.status} ${putResponse.statusText}`);

      await updateFileUrlMutation.mutateAsync({
        input: { reportId: report.id, fieldId: field.id, originalFileName: file.name, captureMethod },
      });

      hasSavedRef.current = true;
      isSavingRef.current = false;
      if (inkVersionRef.current !== inkVersionAtSave) {
        // Strokes landed mid-save: save again once the pen is up.
        setSaveStatus("idle");
        if (!isDrawingRef.current) scheduleAutosave();
        return;
      }
      await reportQuery.refetch();
      if (inkVersionRef.current !== inkVersionAtSave) return;
      clearUnsaved();
      setSaveStatus("saved");
    } catch (error) {
      isSavingRef.current = false;
      console.error("Error saving drawn signature:", error);
      notifyError("Error saving drawn signature. Please try again.");
      setSaveStatus("error");
    } finally {
      unfinishedSavesRef.current -= 1;
      if (!isMountedRef.current && unfinishedSavesRef.current === 0) clearUnsaved();
    }
  };

  const scheduleAutosave = () => {
    cancelAutosave();
    autosaveTimerRef.current = setTimeout(() => {
      autosaveTimerRef.current = null;
      void saveDrawing();
    }, DRAWN_SIGNATURE_AUTOSAVE_DELAY_MS);
  };

  const handlePointerDown = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    const ctx = getContext();
    if (!canvas || !ctx) return;
    // Still drawing: wait for this stroke to end before saving.
    cancelAutosave();
    isDrawingRef.current = true;
    const point = pointFromEvent(canvas, event);
    strokesRef.current.push([point]);
    ctx.beginPath();
    ctx.moveTo(point.x, point.y);
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    const ctx = getContext();
    const stroke = strokesRef.current[strokesRef.current.length - 1];
    if (!canvas || !ctx || !isDrawingRef.current || !stroke) return;
    const point = pointFromEvent(canvas, event);
    const previous = stroke[stroke.length - 1];
    stroke.push(point);
    // Draw only the new segment, so a resize mid-stroke doesn't break the line.
    setPen(ctx);
    ctx.beginPath();
    ctx.moveTo(previous.x, previous.y);
    ctx.lineTo(point.x, point.y);
    ctx.stroke();
    inkVersionRef.current += 1;
    setIsPadActive(true);
    // New ink isn't stored yet, so stop claiming "Saved".
    setSaveStatus((status) => (status === "saved" ? "idle" : status));
  };

  const handlePointerUp = () => {
    if (!isDrawingRef.current) return;
    isDrawingRef.current = false;
    // A tap with no movement leaves no ink.
    strokesRef.current = strokesRef.current.filter((stroke) => stroke.length > 1);
    if (hasInk()) {
      markUnsaved();
      scheduleAutosave();
    }
  };

  const handleClear = () => {
    cancelAutosave();
    clearUnsaved();
    const ctx = getContext();
    if (ctx) ctx.clearRect(0, 0, DRAWN_SIGNATURE_IMAGE_WIDTH, DRAWN_SIGNATURE_IMAGE_HEIGHT);
    strokesRef.current = [];
    setSaveStatus("idle");
    if (hasSavedRef.current) {
      // An earlier autosave stored this drawing; an empty pad must not leave it signable.
      hasSavedRef.current = false;
      void onChange("", {
        onSuccess: async () => {
          await reportQuery.refetch();
        },
        onError: () => {
          hasSavedRef.current = true;
        },
      });
    }
  };

  const isSaving = saveStatus === "saving";

  return (
    <div className="flex flex-col items-start gap-2">
      <div className="w-full rounded-lg border border-dashed border-border-medium bg-white">
        {/* Inside the border, so the canvas itself has the saved image's ratio. */}
        <div className="relative aspect-[8/3] w-full">
          {/* Drawn outside the canvas, so it is never part of the saved image. */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-x-6 bottom-1/4 border-b border-border-strong"
          >
            <span className="absolute left-0 top-1 text-xs text-text-tertiary">Sign here</span>
          </div>
          <canvas
            ref={attachCanvas}
            width={DRAWN_SIGNATURE_IMAGE_WIDTH}
            height={DRAWN_SIGNATURE_IMAGE_HEIGHT}
            className="absolute inset-0 h-full w-full touch-none rounded-lg"
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerLeave={handlePointerUp}
          />
        </div>
      </div>
      <div className="flex items-center gap-3 text-sm">
        <button type="button" className="text-text-secondary underline" onClick={handleClear} disabled={isSaving}>
          Clear
        </button>
        <output className={saveStatus === "error" ? "text-text-error-primary" : "text-text-secondary"}>
          {saveStatus === "saving" && "Saving…"}
          {saveStatus === "saved" && "Saved"}
          {saveStatus === "error" && "Your signature didn't save."}
        </output>
        {saveStatus === "error" && (
          <button
            type="button"
            className="text-interactive-primary-default underline"
            onClick={() => void saveDrawing()}
          >
            Retry
          </button>
        )}
      </div>
    </div>
  );
};
